import { User } from '@microsoft/microsoft-graph-types-beta';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { Status, initialStatus } from './../../../status.interface';
import * as UserActions from './user.actions';

export const featureKey = 'graphUsers';

const avatarBackgrounds = [
    '#75EFFF',
    '#B2CEFF',
    '#D2C299',
    '#FDA3A4',
    '#FDB7F3',
    '#C7B9FF',
    '#A3F89F',
    '#FFB282',
    '#FFDF75',
];
const backgroundLookup = new Map<string, string>();

interface UserStatus extends Status {
    sigInDatesError: boolean;
}

export interface GraphUserWidthAvatarBackground extends User {
    avatarBackground?: string;
}

export interface State extends EntityState<GraphUserWidthAvatarBackground>, UserStatus {
    // additional entities state properties
}

export const adapter: EntityAdapter<GraphUserWidthAvatarBackground> =
    createEntityAdapter<GraphUserWidthAvatarBackground>();

export const initialState: State = adapter.getInitialState({
    // additional entity state properties
    ...initialStatus,
    sigInDatesError: false,
});

export const reducer = createReducer(
    initialState,

    on(UserActions.loadGraphUsersSuccess, (state, action) => {
        const users = action.users;
        const usersWithAvatarBackground = users.map((user) => {
            // assign a random background color
            // but keep it consistent for the same user during the session
            // (so it doesn't change on every re-render)
            // use a map to store the assigned colors
            // use the user id as the key
            // pick a random color from the list if not already assigned
            // store the assigned color in the map
            // use the assigned color in the template
            // (e.g. [ngStyle]="{'background-color': user.avatarBackground}")]}")

            const avatarBackground =
                backgroundLookup.get(user.id) ||
                avatarBackgrounds[Math.floor(Math.random() * avatarBackgrounds.length)];

            backgroundLookup.set(user.id, avatarBackground);

            return {
                ...user,
                avatarBackground,
            };
        });
        return adapter.setAll(usersWithAvatarBackground, { ...state, loading: false, loaded: true });
    }),
    on(UserActions.loadGraphUsersFailure, (state, { error }) => ({ ...state, error, loading: false, loaded: false })),
    on(UserActions.loadGraphUserSignInDatesFailure, (state) => ({ ...state, sigInDatesError: true })),

    on(UserActions.deleteGraphUsers, (state) => ({ ...state, deleting: true })),

    on(UserActions.deleteGraphUsersSuccess, (state, action) =>
        adapter.removeMany(action.ids, { ...state, deleting: false }),
    ),
    on(UserActions.deleteGraphUsersFailure, (state, { error }) => ({ ...state, error, loading: false, loaded: false })),

    on(UserActions.createGraphUser, (state) => ({ ...state, creating: true })),

    on(UserActions.createGraphUsersSuccess, (state, action) => {
        const avatarBackground = avatarBackgrounds[Math.floor(Math.random() * avatarBackgrounds.length)];

        const user = {
            ...action.user,
            avatarBackground,
        };
        backgroundLookup.set(user.id, avatarBackground);

        return adapter.addOne({ ...user }, { ...state, creating: false });
    }),
    on(UserActions.reloadUserWithSignInActivitySuccess, (state, action) =>
        adapter.upsertOne({ ...action.user }, { ...state, updating: false }),
    ),
    on(UserActions.createGraphUsersFailure, (state, { error }) => ({ ...state, error, creating: false })),

    on(UserActions.assignLicense, (state) => ({ ...state, updating: true })),

    on(UserActions.assignLicenseSuccess, (state, action) =>
        adapter.upsertOne({ ...action.user }, { ...state, updating: false }),
    ),
    on(UserActions.assignLicenseFailure, (state, { error }) => ({ ...state, error, updating: false })),

    on(UserActions.updateGraphUserSuccess, (state, action) =>
        adapter.updateOne({ id: action.userId, changes: { ...action.data } }, { ...state, updating: false }),
    ),

    on(UserActions.createGraphUsersWithRole, (state) => ({ ...state, creating: true })),

    on(UserActions.createGraphUsersWithRoleSuccess, (state, action) => {
        const users = action.users;
        const usersWithAvatarBackground = users.map((user) => {
            // assign a random background color
            // but keep it consistent for the same user during the session
            // (so it doesn't change on every re-render)
            // use a map to store the assigned colors
            // use the user id as the key
            // pick a random color from the list if not already assigned
            // store the assigned color in the map
            // use the assigned color in the template
            // (e.g. [ngStyle]="{'background-color': user.avatarBackground}")]}")

            const avatarBackground =
                backgroundLookup.get(user.id) ||
                avatarBackgrounds[Math.floor(Math.random() * avatarBackgrounds.length)];

            backgroundLookup.set(user.id, avatarBackground);

            return {
                ...user,
                avatarBackground,
            };
        });
        return adapter.addMany(usersWithAvatarBackground, { ...state, creating: false });
    }),
);

export const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

export function SystemUserFilter(user: User): boolean {
    return (
        !user.userPrincipalName.includes('urn:spo:anon#') &&
        !user.userPrincipalName.includes('octiga_service_') &&
        !user.userPrincipalName.includes('app@sharepoint') &&
        !user.userPrincipalName.includes('DiscoverySearchMailbox')
    );
}

export function MailboxUserFilter(user: User): boolean {
    return user.mail !== null && user.userType !== 'Guest';
}

export function InternalUsersFilter(user: User): boolean {
    return user.userType !== 'Guest';
}

export const selectGraphUserStatus = (state: any): UserStatus => {
    if (!state) return { ...initialStatus, sigInDatesError: false };
    const { loading, loaded, error, creating, updating, count, updateCount, sigInDatesError } = state;
    return { loading, loaded, error, creating, updating, count, updateCount, sigInDatesError };
};
